import { useEffect, useState } from "react";
import { AppShell } from "../app/AppShell";
import { Alert, Loading } from "../components/Card";
import { PageHead } from "../components/Page";
import { OAuthAskCard, OAuthEndCard } from "../components/OAuthCard";
import { AuthError, authErrText } from "../lib/auth";
import { oauthApi } from "../lib/gitconn";
import type { OAuthRequest } from "../lib/gitconn";
import { useRouteParam } from "../lib/route";
import { tx } from "../i18n";

/* ══ MCP 연결 허용(#/oauth/approve?request=) — 채택 시안 Figma 24:2 ══
   MCP 클라이언트가 b3x-in 주소만 들고 붙으면 api 의 `/oauth/authorize` 가 브라우저를 여기로 보낸다
   (`crates/api/src/mcp_oauth.rs`). 러너 페어링의 요청 카드와 같은 모양 — 누가 · 어디로 돌려보내는지 보고
   러너 이름을 정해 허용한다. 허용 · 거절 모두 **클라이언트의 콜백 주소로 브라우저를 보낸다** — 토큰은
   클라이언트가 그 코드로 직접 받아 가고 이 화면에는 없다. */

type View =
  | { at: "loading" }
  | { at: "ask"; req: OAuthRequest }
  | { at: "end"; kind: "approved" | "denied" | "missing"; name?: string };

export function OAuthApprovePage() {
  const id = useRouteParam("request");
  const [view, setView] = useState<View>({ at: "loading" });
  const [name, setName] = useState("");
  const [busy, setBusy] = useState<"approve" | "deny" | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    setView({ at: "loading" });
    oauthApi
      .request(id)
      .then((req) => {
        if (req.approved) setView({ at: "end", kind: "approved" });
        else {
          setName(req.suggestedName);
          setView({ at: "ask", req });
        }
      })
      .catch((e) => {
        if (e instanceof AuthError && e.status === 404) setView({ at: "end", kind: "missing" });
        else setErr(authErrText(e));
      });
  }, [id]);

  async function approve(req: OAuthRequest) {
    setBusy("approve");
    setErr(null);
    try {
      const r = await oauthApi.approve(req.id, name.trim());
      setView({ at: "end", kind: "approved", name: r.name });
      // 클라이언트로 — 그 주소가 코드를 받아 토큰으로 바꾼다.
      location.href = r.redirect;
    } catch (e) {
      if (e instanceof AuthError && e.status === 404) setView({ at: "end", kind: "missing" });
      else setErr(authErrText(e));
    } finally {
      setBusy(null);
    }
  }

  async function deny(req: OAuthRequest) {
    setBusy("deny");
    setErr(null);
    try {
      const r = await oauthApi.deny(req.id);
      setView({ at: "end", kind: "denied" });
      location.href = r.redirect;
    } catch (e) {
      if (e instanceof AuthError && e.status === 404) setView({ at: "end", kind: "missing" });
      else setErr(authErrText(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <AppShell route="/settings">
      <PageHead title={tx("oauth.title")} />
      {!id ? <Alert tone="info">{tx("oauth.noRequest")}</Alert> : null}
      {id && view.at === "loading" && !err ? <Loading text={tx("oauth.loading")} /> : null}
      {view.at === "ask" ? (
        <OAuthAskCard
          req={view.req}
          name={name}
          onName={setName}
          busy={busy}
          error={err}
          onApprove={() => approve(view.req)}
          onDeny={() => deny(view.req)}
        />
      ) : null}
      {view.at === "end" ? (
        <OAuthEndCard kind={view.kind} name={view.name} />
      ) : null}
      {view.at !== "ask" && err ? <Alert tone="error">{err}</Alert> : null}
    </AppShell>
  );
}
